import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowRight, CalendarDays, Gem, MapPin, Sparkles } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTheme } from "../contexts/ThemeContext";
import ApiURl from "../controllers/Api";

gsap.registerPlugin(ScrollTrigger);

export default function TradeFairParticipation() {
  const { theme } = useTheme();
  const pageRef = useRef(null);
  const [tradeFairs, setTradeFairs] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);

  useEffect(() => {
    let mounted = true;
    fetch(`${ApiURl}getTradeFairs.php?active_only=1`)
      .then((response) => response.json())
      .then((response) => {
        if (!mounted) return;

        if (!response?.success || !Array.isArray(response.data)) {
          setTradeFairs([]);
          return;
        }

        setTradeFairs(
          response.data.map((fair) => ({
            title: fair.title,
            location: fair.location,
            date: fair.date_text,
            image: fair.image_url,
            description: fair.description,
          }))
        );
      })
      .catch((error) => console.error("Unable to load trade fairs:", error));

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    fetch(`${ApiURl}getTradeFairGallery.php?active_only=1`)
      .then((response) => response.json())
      .then((response) => {
        if (!mounted) return;

        if (!response?.success || !Array.isArray(response.data)) {
          setGalleryItems([]);
          return;
        }

        setGalleryItems(
          response.data
            .filter((item) => item.image_url)
            .map((item) => ({
              title: item.title || "Trade Fair Gallery",
              image: item.image_url,
            }))
        );
      })
      .catch((error) => console.error("Unable to load trade fair gallery:", error));

    return () => {
      mounted = false;
    };
  }, []);

  useLayoutEffect(() => {
    const root = pageRef.current;
    if (!root) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const ctx = gsap.context(() => {
      const hero = root.querySelector("[data-trade-hero]");
      const cards = gsap.utils.toArray(
        root.querySelectorAll("[data-trade-card]")
      );
      const galleryCards = gsap.utils.toArray(
        root.querySelectorAll("[data-trade-gallery]")
      );

      if (reduceMotion) {
        gsap.set([hero, ...cards, ...galleryCards], {
          autoAlpha: 1,
          x: 0,
          y: 0,
          clearProps: "transform",
        });
        return;
      }

      if (hero) {
        gsap.fromTo(
          hero,
          { autoAlpha: 0, y: 26 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            overwrite: "auto",
          }
        );
      }

      cards.forEach((card, index) => {
        gsap.fromTo(
          card,
          { autoAlpha: 0, y: 34 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.72,
            delay: (index % 2) * 0.08,
            ease: "power3.out",
            overwrite: "auto",
            scrollTrigger: {
              trigger: card,
              start: "top 92%",
              once: true,
              invalidateOnRefresh: true,
            },
          }
        );
      });

      galleryCards.forEach((card, index) => {
        gsap.fromTo(
          card,
          { autoAlpha: 0, y: 24 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            delay: Math.min(index, 3) * 0.06,
            ease: "power3.out",
            overwrite: "auto",
            scrollTrigger: {
              trigger: card,
              start: "top 92%",
              once: true,
              invalidateOnRefresh: true,
            },
          }
        );
      });

      const refreshTimer = window.setTimeout(() => {
        ScrollTrigger.refresh();
      }, 120);

      const handleLoad = () => {
        ScrollTrigger.refresh();
      };

      window.addEventListener("load", handleLoad);

      return () => {
        window.clearTimeout(refreshTimer);
        window.removeEventListener("load", handleLoad);
      };
    }, root);

    return () => {
      ctx.revert();
    };
  }, [tradeFairs, galleryItems]);

  return (
    <main
      ref={pageRef}
      className="min-h-screen overflow-hidden font-[Kanit]"
      style={{ backgroundColor: theme.user_bg, color: theme.user_text }}
    >
      <section className="relative px-5 py-16 sm:px-8 lg:px-20 lg:py-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(circle at 12% 12%, rgba(255,255,255,0.75), transparent 30%), radial-gradient(circle at 86% 22%, rgba(212,175,55,0.18), transparent 28%)",
          }}
        />

        <div data-trade-hero className="relative mx-auto max-w-6xl text-center">
          <div className="mx-auto flex w-fit items-center gap-4">
            <span className="h-px w-16" style={{ backgroundColor: theme.user_gold }} />
            <Sparkles className="h-5 w-5" style={{ color: theme.user_gold }} />
            <span className="h-px w-16" style={{ backgroundColor: theme.user_gold }} />
          </div>

          <p className="mt-6 text-xs font-bold uppercase tracking-[0.35em]" style={{ color: theme.user_gold }}>
            Trade Fair Participation
          </p>
          <h1 className="mt-4 font-serif text-5xl leading-tight sm:text-6xl lg:text-7xl" style={{ color: theme.user_maroon }}>
            Our Jewellery Showcase Journey
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-base leading-8 sm:text-lg" style={{ color: theme.user_text_soft }}>
            From Mumbai to Hyderabad, Kolkata and Bengaluru, SL Gold continues to present its craftsmanship at India's leading jewellery exhibitions.
          </p>
        </div>
      </section>

      <section data-trade-list className="px-5 pb-14 sm:px-8 lg:px-20">
        <div className="mx-auto grid max-w-7xl gap-5 lg:grid-cols-2">
          {tradeFairs.map((fair, index) => (
            <article
              key={fair.title}
              data-trade-card
              className="group rounded-[26px] border p-5 shadow-[0_18px_55px_rgba(43,0,0,0.08)] transition duration-300 hover:-translate-y-1"
              style={{ backgroundColor: theme.user_card, borderColor: theme.user_border }}
            >
              {fair.image && (
                <div className="mb-5 overflow-hidden rounded-[20px]">
                  <img
                    src={fair.image}
                    alt={fair.title}
                    className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-64"
                    onError={(event) => {
                      event.currentTarget.parentElement.style.display = "none";
                    }}
                  />
                </div>
              )}

              <div className="flex items-start gap-4">
                <span
                  className="grid h-12 w-12 shrink-0 place-items-center rounded-full font-serif text-xl font-bold"
                  style={{ backgroundColor: theme.user_maroon, color: theme.user_gold }}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2 className="font-serif text-2xl leading-tight sm:text-3xl" style={{ color: theme.user_maroon }}>
                    {fair.title}
                  </h2>
                  <div className="mt-4 flex flex-wrap gap-3 text-sm" style={{ color: theme.user_text_soft }}>
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="h-4 w-4" style={{ color: theme.user_gold }} />
                      {fair.location}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" style={{ color: theme.user_gold }} />
                      {fair.date}
                    </span>
                  </div>
                  <p className="mt-5 text-sm leading-7 sm:text-[15px]" style={{ color: theme.user_text_soft }}>
                    {fair.description}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section data-gallery-wrap className="px-5 pb-20 sm:px-8 lg:px-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-9 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.32em]" style={{ color: theme.user_gold }}>
                Gallery
              </p>
              <h2 className="mt-2 font-serif text-4xl sm:text-5xl" style={{ color: theme.user_maroon }}>
                Glimpses From Trade Fairs
              </h2>
            </div>
            <div className="hidden items-center gap-2 sm:flex" style={{ color: theme.user_gold }}>
              <span className="h-px w-20" style={{ backgroundColor: theme.user_gold }} />
              <Gem className="h-5 w-5" />
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {galleryItems.map((item) => (
              <article
                key={`${item.title}-${item.image}`}
                data-trade-gallery
                className="overflow-hidden rounded-[22px] border shadow-[0_16px_45px_rgba(43,0,0,0.08)]"
                style={{ backgroundColor: theme.user_card, borderColor: theme.user_border }}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="h-56 w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.parentElement.style.display = "none";
                  }}
                />
                <div className="p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: theme.user_gold }}>
                    Trade Fair
                  </p>
                  <h3 className="mt-2 font-serif text-xl leading-snug" style={{ color: theme.user_maroon }}>
                    {item.title}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: theme.user_maroon }}>
                    View showcase <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}